import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BackOfficeMenu from '../../components/BackOfficeMenu';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../../utils/usePermissions';
import { 
  ChevronLeft, 
  Wallet, 
  Clock, 
  Check, 
  X, 
  AlertCircle, 
  RefreshCw, 
  Plus, 
  Trash2, 
  History, 
  FileText,
  CreditCard,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import '../GestionRH.css';
import '../employes/DetailEmploye.css';
import './DetailSalaire.css';

const apiBase = () => {
  const base = import.meta.env.VITE_API_URL || '/api';
  return base.endsWith('/api') ? base : `${base}/api`;
};
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtMontant = (n) => n != null ? `${Number(n).toLocaleString('fr-TN', { minimumFractionDigits: 3 })} TND` : '—';
const fmtFull = (d) => d ? new Date(d).toLocaleString('fr-FR') : '—';

const MOIS_LABELS = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const SOURCE_LABELS = {
  anciennete: '🎖️ Ancienneté', assiduite: '⭐ Assiduité', absence: '⚠️ Absence',
  performance: '📈 Performance', evaluation: '📋 Évaluation', manuelle: '✋ Manuelle'
};

const TABS = [
  { id: 'recap', label: 'Récapitulatif', icon: <Wallet size={16} /> },
  { id: 'primes', label: 'Primes & Déductions', icon: <Plus size={16} /> },
  { id: 'audit', label: 'Audit & Historique', icon: <History size={16} /> }
];

export default function DetailSalaire({ salaireId, onNavigate, onLogout, currentPage }) {
  const { hasPermission } = usePermissions();
  const [salaire, setSalaire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('recap');
  const [recalculating, setRecalculating] = useState(false);

  const [showPrimeModal, setShowPrimeModal] = useState(false);
  const [showDeductionModal, setShowDeductionModal] = useState(false);
  const [showBaseModal, setShowBaseModal] = useState(false);
  const [showValiderPrimeModal, setShowValiderPrimeModal] = useState(false);
  const [selectedPrime, setSelectedPrime] = useState(null);

  const [primeForm, setPrimeForm] = useState({ libelle: '', montant: '', source: 'manuelle' });
  const [deductionForm, setDeductionForm] = useState({ libelle: '', montant: '' });
  const [nouvelleBase, setNouvelleBase] = useState('');
  const [validerPrimeForm, setValiderPrimeForm] = useState({ statut: 'validee', montant: '' });

  useEffect(() => {
    if (!salaireId) { onNavigate('rh-salaires'); return; }
    fetchSalaire();
  }, [salaireId]);

  const fetchSalaire = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${apiBase()}/rh/salaires/${salaireId}`, { headers: headers() });
      setSalaire(data);
      setNouvelleBase(data.salaireBase);
    } catch { setError('Salaire introuvable'); }
    finally { setLoading(false); }
  };

  const flash = (msg, isError = false) => {
    if (isError) setError(msg); else setMessage(msg);
    setTimeout(() => { setError(null); setMessage(null); }, 4500);
  };

  const isPaye = salaire?.statut === 'paye';

  const handleChangerStatut = async (nextStatut) => {
    try {
      await axios.patch(`${apiBase()}/rh/salaires/${salaireId}/statut`, { statut: nextStatut }, { headers: headers() });
      flash(`Statut mis à jour`);
      fetchSalaire();
    } catch (err) { flash(err.response?.data?.message || 'Erreur', true); }
  };

  const handleUpdateBase = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${apiBase()}/rh/salaires/${salaireId}/base`, { salaireBase: Number(nouvelleBase) }, { headers: headers() });
      setShowBaseModal(false);
      flash('Base salariale mise à jour');
      fetchSalaire();
    } catch (err) { flash(err.response?.data?.message || 'Erreur', true); }
  };

  const handleRecalculer = async () => {
    setRecalculating(true);
    try {
      await axios.post(`${apiBase()}/rh/salaires/${salaireId}/recalculer-primes`, {}, { headers: headers() });
      flash('Primes auto recalculées');
      fetchSalaire();
    } catch (err) { flash(err.response?.data?.message || 'Erreur', true); }
    finally { setRecalculating(false); }
  };

  const handleAjouterPrime = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${apiBase()}/rh/salaires/${salaireId}/primes`, { ...primeForm, montant: Number(primeForm.montant) }, { headers: headers() });
      setShowPrimeModal(false);
      setPrimeForm({ libelle: '', montant: '', source: 'manuelle' });
      flash('Prime ajoutée');
      fetchSalaire();
    } catch (err) { flash(err.response?.data?.message || 'Erreur', true); }
  };

  const handleSupprimerPrime = async (primeId) => {
    if (!window.confirm('Supprimer cette prime ?')) return;
    try {
      await axios.delete(`${apiBase()}/rh/salaires/${salaireId}/primes/${primeId}`, { headers: headers() });
      flash('Prime supprimée');
      fetchSalaire();
    } catch (err) { flash(err.response?.data?.message || 'Erreur', true); }
  };

  const handleValiderPrime = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${apiBase()}/rh/salaires/${salaireId}/primes/${selectedPrime._id}`,
        { statut: validerPrimeForm.statut, montant: validerPrimeForm.montant ? Number(validerPrimeForm.montant) : undefined },
        { headers: headers() }
      );
      setShowValiderPrimeModal(false);
      flash(`Prime validée/rejetée`);
      fetchSalaire();
    } catch (err) { flash(err.response?.data?.message || 'Erreur', true); }
  };

  const handleAjouterDeduction = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${apiBase()}/rh/salaires/${salaireId}/deductions`, { ...deductionForm, montant: Number(deductionForm.montant) }, { headers: headers() });
      setShowDeductionModal(false);
      setDeductionForm({ libelle: '', montant: '' });
      flash('Déduction ajoutée');
      fetchSalaire();
    } catch (err) { flash(err.response?.data?.message || 'Erreur', true); }
  };

  const handleSupprimerDeduction = async (dedId) => {
    if (!window.confirm('Supprimer cette déduction ?')) return;
    try {
      await axios.delete(`${apiBase()}/rh/salaires/${salaireId}/deductions/${dedId}`, { headers: headers() });
      flash('Déduction supprimée');
      fetchSalaire();
    } catch (err) { flash(err.response?.data?.message || 'Erreur', true); }
  };

  if (loading) return (
    <div className="rh-dashboard-container">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
      <div className="rh-dashboard-content"><LoadingSpinner /></div>
    </div>
  );

  if (error || !salaire) return (
    <div className="rh-dashboard-container">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
      <div className="rh-dashboard-content">
        <div className="rh-table-card" style={{ textAlign: 'center', padding: '50px' }}>
          <h2 style={{ color: 'var(--rh-danger)' }}>{error || 'Salaire non trouvé'}</h2>
          <button className="rh-add-btn" style={{ margin: '20px auto' }} onClick={() => onNavigate('rh-salaires')}>
            <ChevronLeft size={20} /> Retour
          </button>
        </div>
      </div>
    </div>
  );

  const primesValidees = salaire.primes?.filter(p => p.statut === 'validee') || [];
  const primesProposees = salaire.primes?.filter(p => p.statut === 'proposee') || [];
  const totalPrimes = primesValidees.reduce((s, p) => s + (p.montant || 0), 0);
  const totalDed = salaire.deductions?.reduce((s, d) => s + (d.montant || 0), 0) || 0;

  return (
    <div className="rh-dashboard-container">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
      <div className="rh-dashboard-content">
        
        {/* Header Hero Section */}
        <div className="rh-detail-header animate-fade-in">
          <div className="rh-detail-identity">
            <button className="rh-detail-back-btn" onClick={() => onNavigate('rh-salaires')}>
              <ChevronLeft size={18} /> Retour
            </button>
            <div className="rh-detail-avatar">
              {salaire.employe?.prenom?.[0]}{salaire.employe?.nom?.[0]}
            </div>
            <div className="rh-detail-info">
              <h1>{salaire.employe?.prenom} {salaire.employe?.nom}</h1>
              <p>Salaire {MOIS_LABELS[salaire.mois]} {salaire.annee} · {salaire.employe?.poste?.nom}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <span className={`rh-status-badge ${salaire.statut === 'paye' ? 'approved' : salaire.statut === 'valide' ? 'info' : 'pending'}`}>
              {salaire.statut}
            </span>
            {!isPaye && (
              <div style={{ display: 'flex', gap: '10px' }}>
                {salaire.statut === 'brouillon' && (
                  <button className="rh-add-btn" onClick={() => handleChangerStatut('valide')}>
                    <Check size={16} /> Valider
                  </button>
                )}
                {salaire.statut === 'valide' && (
                  <button className="rh-add-btn" style={{ background: 'var(--rh-blue-dark)', color: 'white' }} onClick={() => handleChangerStatut('paye')}>
                    <CreditCard size={16} /> Marquer payé
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {message && <div className="rh-alert rh-alert-success animate-fade-in">{message}</div>}

        {primesProposees.length > 0 && (
          <div className="sal-pending-banner animate-fade-in">
            <AlertCircle size={20} />
            <div>
              <strong>{primesProposees.length} prime(s) automatique(s) en attente</strong> — Ajustez et validez dans l'onglet Primes.
            </div>
          </div>
        )}

        {/* Tabs Navigation */}
        <div className="rh-detail-tabs animate-fade-in">
          {TABS.map((tab) => (
            <button 
              key={tab.id} 
              className={`rh-detail-tab ${activeTab === tab.id ? 'active' : ''}`} 
              onClick={() => setActiveTab(tab.id)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {tab.icon}
                {tab.label}
              </div>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="animate-fade-in">
          {activeTab === 'recap' && (
            <div className="sal-recap-grid">
              <div className="rh-detail-card">
                <h3><Wallet size={18} /> Salaire de base</h3>
                <div className="sal-big-amount">{fmtMontant(salaire.salaireBase)}</div>
                {!isPaye && (
                  <button className="rh-view-all" style={{ marginTop: '15px', border: 'none', background: 'none', cursor: 'pointer' }} onClick={() => setShowBaseModal(true)}>
                    Modifier la base
                  </button>
                )}
              </div>
              <div className="rh-detail-card">
                <h3><TrendingUp size={18} /> Primes validées</h3>
                <div className={`sal-big-amount ${totalPrimes >= 0 ? 'sal-bonus-text' : 'sal-malus-text'}`}>
                  {totalPrimes >= 0 ? '+' : ''}{fmtMontant(totalPrimes)}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--rh-text-muted)', marginTop: '5px' }}>{primesValidees.length} élément(s)</div>
              </div>
              <div className="rh-detail-card">
                <h3><TrendingDown size={18} /> Déductions</h3>
                <div className="sal-big-amount sal-malus-text">-{fmtMontant(totalDed)}</div>
                <div style={{ fontSize: '13px', color: 'var(--rh-text-muted)', marginTop: '5px' }}>{salaire.deductions?.length || 0} élément(s)</div>
              </div>
              <div className="rh-detail-card sal-net-card">
                <h3><CreditCard size={18} /> Net à payer</h3>
                <div className="sal-big-amount sal-net-big">{fmtMontant(salaire.salaireNet)}</div>
                <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '5px' }}>Total virement</div>
              </div>
            </div>
          )}

          {activeTab === 'primes' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
              <div className="rh-table-card">
                <div className="rh-table-header">
                  <h2>Primes & Éléments Variable</h2>
                  {!isPaye && (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button className="rh-action-btn" onClick={handleRecalculer} title="Recalculer auto">
                        <RefreshCw size={16} className={recalculating ? 'animate-spin' : ''} />
                      </button>
                      <button className="rh-add-btn" onClick={() => setShowPrimeModal(true)}>
                        <Plus size={16} /> Ajouter une prime
                      </button>
                    </div>
                  )}
                </div>
                <div className="rh-table-container">
                  <table className="rh-table">
                    <thead>
                      <tr>
                        <th>Libellé</th>
                        <th>Source</th>
                        <th>Montant</th>
                        <th>Statut</th>
                        <th style={{ textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salaire.primes?.map(p => (
                        <tr key={p._id}>
                          <td><strong>{p.libelle}</strong></td>
                          <td><span className="rh-type-badge">{SOURCE_LABELS[p.source] || p.source}</span></td>
                          <td>
                            <span className={p.montant < 0 ? 'sal-malus-text' : 'sal-bonus-text'}>
                              {p.montant >= 0 ? '+' : ''}{fmtMontant(p.montant)}
                            </span>
                          </td>
                          <td>
                            <span className={`rh-status-badge ${p.statut === 'validee' ? 'approved' : p.statut === 'rejetee' ? 'rejected' : 'pending'}`}>
                              {p.statut}
                            </span>
                          </td>
                          <td>
                            <div className="rh-actions">
                              {!isPaye && p.statut === 'proposee' && (
                                <button className="rh-action-btn edit" onClick={() => { setSelectedPrime(p); setValiderPrimeForm({ statut: 'validee', montant: p.montant }); setShowValiderPrimeModal(true); }}>
                                  <Check size={16} />
                                </button>
                              )}
                              {!isPaye && (
                                <button className="rh-action-btn delete" onClick={() => handleSupprimerPrime(p._id)}>
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {(!salaire.primes || salaire.primes.length === 0) && (
                        <tr><td colSpan="5" style={{ textAlign: 'center', padding: '30px' }}>Aucune prime enregistrée.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rh-table-card">
                <div className="rh-table-header">
                  <h2>Déductions & Retenues</h2>
                  {!isPaye && (
                    <button className="rh-add-btn" onClick={() => setShowDeductionModal(true)}>
                      <Plus size={16} /> Ajouter une déduction
                    </button>
                  )}
                </div>
                <div className="rh-table-container">
                  <table className="rh-table">
                    <thead>
                      <tr>
                        <th>Libellé</th>
                        <th>Montant</th>
                        <th style={{ textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salaire.deductions?.map(d => (
                        <tr key={d._id}>
                          <td><strong>{d.libelle}</strong></td>
                          <td><span className="sal-malus-text">-{fmtMontant(d.montant)}</span></td>
                          <td>
                            <div className="rh-actions">
                              {!isPaye && (
                                <button className="rh-action-btn delete" onClick={() => handleSupprimerDeduction(d._id)}>
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {(!salaire.deductions || salaire.deductions.length === 0) && (
                        <tr><td colSpan="3" style={{ textAlign: 'center', padding: '30px' }}>Aucune déduction enregistrée.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="rh-detail-grid">
              <div className="rh-detail-card">
                <h3><FileText size={18} /> Métadonnées</h3>
                <div className="rh-detail-rows">
                  <div className="rh-detail-row"><span>Créé par</span><strong>{salaire.creePar ? `${salaire.creePar.prenom} ${salaire.creePar.nom}` : 'Système'}</strong></div>
                  <div className="rh-detail-row"><span>Date création</span><strong>{fmtFull(salaire.createdAt)}</strong></div>
                  <div className="rh-detail-row"><span>Validé par</span><strong>{salaire.validerPar ? `${salaire.validerPar.prenom} ${salaire.validerPar.nom}` : '—'}</strong></div>
                  <div className="rh-detail-row"><span>Date validation</span><strong>{salaire.dateValidation ? fmtFull(salaire.dateValidation) : '—'}</strong></div>
                </div>
              </div>
              <div className="rh-detail-card">
                <h3><History size={18} /> Journal d'audit</h3>
                <div className="rh-audit-timeline">
                  {[...(salaire.historique || [])].reverse().map((h, i) => (
                    <div key={i} className="rh-audit-item">
                      <div className="rh-audit-dot" />
                      <div className="rh-audit-content">
                        <div className="rh-audit-action">{h.details}</div>
                        <div className="rh-audit-meta">
                          {h.auteur ? `${h.auteur.prenom} ${h.auteur.nom}` : 'Système'} · {fmtFull(h.date)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modals Section */}
        {showBaseModal && (
          <div className="rh-modal-overlay" onClick={() => setShowBaseModal(false)}>
            <div className="rh-modal" onClick={e => e.stopPropagation()}>
              <div className="rh-modal-header">
                <h2>Modifier le salaire de base</h2>
                <button className="rh-modal-close" onClick={() => setShowBaseModal(false)}><X size={20} /></button>
              </div>
              <form onSubmit={handleUpdateBase}>
                <div className="rh-form-group">
                  <label>Nouveau montant (TND)</label>
                  <input required type="number" min="0" step="0.001" value={nouvelleBase} onChange={e => setNouvelleBase(e.target.value)} />
                </div>
                <div className="rh-modal-footer">
                  <button type="button" className="rh-btn-cancel" onClick={() => setShowBaseModal(false)}>Annuler</button>
                  <button type="submit" className="rh-btn-submit">Mettre à jour</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showPrimeModal && (
          <div className="rh-modal-overlay" onClick={() => setShowPrimeModal(false)}>
            <div className="rh-modal" onClick={e => e.stopPropagation()}>
              <div className="rh-modal-header">
                <h2>Ajouter une prime</h2>
                <button className="rh-modal-close" onClick={() => setShowPrimeModal(false)}><X size={20} /></button>
              </div>
              <form onSubmit={handleAjouterPrime}>
                <div className="rh-form-group">
                  <label>Libellé de la prime</label>
                  <input required value={primeForm.libelle} onChange={e => setPrimeForm({ ...primeForm, libelle: e.target.value })} placeholder="Ex: Prime exceptionnelle" />
                </div>
                <div className="rh-form-row">
                  <div className="rh-form-group">
                    <label>Montant (TND)</label>
                    <input required type="number" step="0.001" value={primeForm.montant} onChange={e => setPrimeForm({ ...primeForm, montant: e.target.value })} />
                  </div>
                  <div className="rh-form-group">
                    <label>Source</label>
                    <select value={primeForm.source} onChange={e => setPrimeForm({ ...primeForm, source: e.target.value })}>
                      {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>
                <div className="rh-modal-footer">
                  <button type="button" className="rh-btn-cancel" onClick={() => setShowPrimeModal(false)}>Annuler</button>
                  <button type="submit" className="rh-btn-submit">Ajouter</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showValiderPrimeModal && selectedPrime && (
          <div className="rh-modal-overlay" onClick={() => setShowValiderPrimeModal(false)}>
            <div className="rh-modal" onClick={e => e.stopPropagation()}>
              <div className="rh-modal-header">
                <h2>Validation de prime</h2>
                <button className="rh-modal-close" onClick={() => setShowValiderPrimeModal(false)}><X size={20} /></button>
              </div>
              <form onSubmit={handleValiderPrime}>
                <div style={{ marginBottom: '20px', padding: '15px', background: 'var(--rh-light-gray)', borderRadius: '12px' }}>
                  <div style={{ fontSize: '13px', color: 'var(--rh-text-muted)' }}>Prime automatique</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--rh-blue-dark)' }}>{selectedPrime.libelle}</div>
                  <div style={{ fontSize: '18px', fontWeight: '800', color: 'var(--rh-success)', marginTop: '5px' }}>{fmtMontant(selectedPrime.montant)}</div>
                </div>
                <div className="rh-form-group">
                  <label>Modifier le montant final (optionnel)</label>
                  <input type="number" step="0.001" value={validerPrimeForm.montant} onChange={e => setValiderPrimeForm({ ...validerPrimeForm, montant: e.target.value })} placeholder="Laisser vide pour garder le montant proposé" />
                </div>
                <div className="rh-form-group">
                  <label>Décision</label>
                  <div className="dem-decision-btns">
                    <button type="button" className={`dem-decision-btn dem-ok ${validerPrimeForm.statut === 'validee' ? 'active' : ''}`} onClick={() => setValiderPrimeForm({ ...validerPrimeForm, statut: 'validee' })}>
                      Accepter
                    </button>
                    <button type="button" className={`dem-decision-btn dem-refuse ${validerPrimeForm.statut === 'rejetee' ? 'active' : ''}`} onClick={() => setValiderPrimeForm({ ...validerPrimeForm, statut: 'rejetee' })}>
                      Rejeter
                    </button>
                  </div>
                </div>
                <div className="rh-modal-footer">
                  <button type="button" className="rh-btn-cancel" onClick={() => setShowValiderPrimeModal(false)}>Annuler</button>
                  <button type="submit" className="rh-btn-submit">Confirmer la décision</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showDeductionModal && (
          <div className="rh-modal-overlay" onClick={() => setShowDeductionModal(false)}>
            <div className="rh-modal" onClick={e => e.stopPropagation()}>
              <div className="rh-modal-header">
                <h2>Ajouter une déduction</h2>
                <button className="rh-modal-close" onClick={() => setShowDeductionModal(false)}><X size={20} /></button>
              </div>
              <form onSubmit={handleAjouterDeduction}>
                <div className="rh-form-group">
                  <label>Libellé de la retenue</label>
                  <input required value={deductionForm.libelle} onChange={e => setDeductionForm({ ...deductionForm, libelle: e.target.value })} placeholder="Ex: Avance sur salaire" />
                </div>
                <div className="rh-form-group">
                  <label>Montant (TND)</label>
                  <input required type="number" min="0" step="0.001" value={deductionForm.montant} onChange={e => setDeductionForm({ ...deductionForm, montant: e.target.value })} />
                </div>
                <div className="rh-modal-footer">
                  <button type="button" className="rh-btn-cancel" onClick={() => setShowDeductionModal(false)}>Annuler</button>
                  <button type="submit" className="rh-btn-submit">Ajouter</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
